/**
 * Deck construction and shuffling.
 *
 * Pure data — no DOM, no Three.js, no crypto beyond an optional entropy source
 * for the default seed. Every shuffle runs through a seedable PRNG so a game can
 * be replayed exactly by re-using its seed (the engine exposes the seed it used).
 */

import type { CardId, Rank, Suit } from '@/game/types';

/** Suit order used when building the ordered deck. Matches the original app. */
export const SUITS: readonly Suit[] = ['c', 's', 'h', 'd'] as const;

/** Rank order used when building the ordered deck. */
export const RANKS: readonly Rank[] = [
  'a',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  'j',
  'q',
  'k',
] as const;

export const DECK_SIZE = SUITS.length * RANKS.length;

/** A uniform random source in [0, 1). */
export type RandomFn = () => number;

/**
 * mulberry32 — 32-bit, seedable, fast, and good enough for card shuffling.
 * Deterministic for a given seed on every platform, which is the whole point.
 */
export function mulberry32(seed: number): RandomFn {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A fresh 32-bit seed from the platform CSPRNG when there is one, falling back
 * to `Math.random` in exotic environments.
 */
export function randomSeed(): number {
  const source = globalThis.crypto;
  if (source && typeof source.getRandomValues === 'function') {
    const buffer = new Uint32Array(1);
    source.getRandomValues(buffer);
    // Length 1, just filled.
    return buffer[0]! >>> 0;
  }
  return Math.floor(Math.random() * 0x1_0000_0000) >>> 0;
}

/** A PRNG for `seed`, or one seeded from platform entropy when omitted. */
export function makeRng(seed?: number): RandomFn {
  return mulberry32(seed ?? randomSeed());
}

/** The 52 card ids in canonical order: `ac`, `2c` … `ks`, `ah` … `kd`. */
export function createDeck(): CardId[] {
  const deck: CardId[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push(`${rank}${suit}`);
    }
  }
  return deck;
}

/** Fisher–Yates. Returns a new array; the input is never mutated. */
export function shuffle<T>(items: readonly T[], rng: RandomFn = Math.random): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    // `i` walks the array and `j` is bounded by it, so both are in range.
    const a = out[i]!;
    const b = out[j]!;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

/**
 * A shuffled 52-card deck. Index 0 is the first card the player will be dealt,
 * which is the ordering the engine's stock uses.
 */
export function createShuffledDeck(seed?: number): CardId[] {
  return shuffle(createDeck(), makeRng(seed));
}
